import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import CrossIcon from 'src/assets/images/CloseProduct.svg';
import Button from 'src/common/refactoredComponents/button/Button';
import { ButtonStyles } from 'src/constants/buttonStyles';
import RadioButton from 'src/common/components/form/radioButton/RadioButton';
import {
    setShowNoUsageToReportOrCancelPopup,
    cancelUsageOrderRequest,
    fetchUsageDetailsRequest,
    getDropdownOptionsRequest,
    getProcedureInfoRequest,
} from 'src/pages/reportUsage/redux/action/actionCreators';
import Spinner from 'src/common/refactoredComponents/spinner/Spinner';
import {
    DialogContentContainer,
    NoUsageHeader,
    NoUsageTitle,
    NoUsageReasonsDiv,
    NoUsageReasons,
    NoUsageButtonContainer,
    CrossIconImg,
    BackButton,
    NoUsageFooterContainer,
} from './noUsageToReport.styled';
import ArrowLeft from 'src/assets/images/LeftArrowBlue.svg';
import { StyledTextDiv } from 'src/pages/reportUsage/utils/reportUsage.styled';
import { getRoutePathWithParams } from 'src/utils/util';
import { sizes } from 'src/styles/Media.styled';
import {
    closeOrderSummaryModalState,
    setOrderSummaryModalState,
    toggleSearchResult,
} from 'src/pages/orders/redux/actions';
import { unlockOrderFunc } from 'src/pages/reportUsage/redux/saga/dataServices';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';
import { useTranslation } from 'react-i18next';
import { VISIBLE } from '../../../../constants/appConstants';
import { setPediatricCheck } from 'src/common/redux/actions';
import { resetDropdown } from '../../../scheduleSurgery/redux/actions';

const NoUsageReasonsContainer = forwardRef(({
    cancelSurgeryData,
    backBtnClickHandler,
    isFromOrderDetailsModal,
}, ref) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { t } = useTranslation();

    const { unidPlanStatus, loading, showSearchModal } = useSelector(
        (state) => ({
            unidPlanStatus:
                state.orderSummaryReducer.orderBasicDetails?.procedureDetail
                    ?.unidPlanStatus ? state.orderSummaryReducer.orderBasicDetails?.procedureDetail
                    ?.unidPlanStatus : cancelSurgeryData?.unidPlanDetails,
            loading: state.orderSummaryReducer.loading,
            showSearchModal: state.appliedOrdersSearchReducer.showSearchModal,
        })
    );

    const {
        caseCancelledVisibility,
        competitorChosenVisibility,
        usageViaEquityOrderVisibility,
        instrumentOnlySurgeryVisibility,
    } = useSelector((state) => state.loginReducer);

    const [selectedReason, setSelectedReason] = useState(
        unidPlanStatus
            ? unidPlanStatus === 'Approved' ||
              unidPlanStatus === 'Manufacturing Complete' ||
              unidPlanStatus === 'In Manufacturing'
                ? {
                      optionId: 'UNID_NON_USE',
                      optionLabel: 'UNiD Non-Use',
                      showProcedureScreen: true,
                  }
                : null
            : null
    );

    const noUsageReasons = [
        {
            optionId: 'NON_USE_CANCELLED',
            optionLabel: t('noUsageToReport.noUsageReasons.caseCancelled') ,
            showProcedureScreen: false,
            visibility: caseCancelledVisibility === VISIBLE,
        },
        {
            optionId: 'NON_USE_MISC',
            optionLabel:t('noUsageToReport.noUsageReasons.usageViaEquityOrder') ,
            showProcedureScreen: true,
            visibility: usageViaEquityOrderVisibility === VISIBLE,
        },
        {
            optionId: 'NON_USE_COMPETITOR',
            optionLabel: t('noUsageToReport.noUsageReasons.competitorChosen'),
            showProcedureScreen: false,
            visibility: competitorChosenVisibility === VISIBLE,
        },
        {
            optionId: 'NON_USE_INSTRUMENTS',
            optionLabel: t('noUsageToReport.noUsageReasons.instrumentOnlySurgery'),
            showProcedureScreen: true,
            visibility: instrumentOnlySurgeryVisibility === VISIBLE,
        },
        {
            optionId: 'UNID_NON_USE',
            optionLabel: t('noUsageToReport.noUsageReasons.unidNonUse'),
            showProcedureScreen: true,
            visibility: true,
        },
    ];

    useEffectWithErrorLogging(() => {
        if (
            unidPlanStatus &&
            (unidPlanStatus === 'Approved' ||
                unidPlanStatus === 'Manufacturing Complete' ||
                unidPlanStatus === 'In Manufacturing')
        ) {
            setSelectedReason({
                optionId: 'UNID_NON_USE',
                optionLabel: 'UNiD Non-Use',
                showProcedureScreen: true,
                visibility: true,
            });
        }
    }, [unidPlanStatus]);

    const closeNoUsageToReportPopup = (viewProcedure) => {
        dispatch(
            setShowNoUsageToReportOrCancelPopup({
                showNoUsagePopup: false,
                showCancelPopup: false,
                surgeryData: viewProcedure ? cancelSurgeryData : {},
                viewProcedureScreen: viewProcedure,
                cancelReason: viewProcedure ? selectedReason : null,
                isFromOrderDetailsModal,
                showCaseCancelOrPostpone: false,
            })
        );
    };

    const submitBtnClickHandler = () => {
        dispatch(resetDropdown());
        dispatch(setPediatricCheck(''));
        if (selectedReason?.showProcedureScreen) {
            dispatch(
                setOrderSummaryModalState({
                    myCaseRef: cancelSurgeryData?.myCaseRef,
                    orderSummaryModal: false,
                })
            );
            closeNoUsageToReportPopup(true);
            dispatch(
                getDropdownOptionsRequest('physicians', {
                    physicianType: 'All',
                    searchTerm: cancelSurgeryData?.physicianDisplayName
                        ? cancelSurgeryData?.physicianDisplayName
                        : '',
                })
            );

            dispatch(
                fetchUsageDetailsRequest({
                    resourceType: 'reportUsage',
                    myCaseRef: cancelSurgeryData?.myCaseRef,
                })
            );

            history.push(getRoutePathWithParams('/nutrprocedure'));
        } else if (cancelSurgeryData?.myCaseRef && selectedReason) {
            closeNoUsageToReportPopup(true);
            dispatch(
                fetchUsageDetailsRequest({
                    resourceType: 'reportUsage',
                    myCaseRef: cancelSurgeryData?.myCaseRef,
                })
            );
            history.push(getRoutePathWithParams('/nutrprocedure'));

            dispatch(
                cancelUsageOrderRequest({
                    cancelReason: selectedReason.optionId,
                    myCaseRef: cancelSurgeryData.myCaseRef,
                })
            );
        }
    };
    const redirectToCalendarOrOrdersPage = () => {
        (cancelSurgeryData?.myCaseRef && cancelSurgeryData?.myCaseRef?.length > 0) && unlockOrderFunc({
            action: 'NoUsageToReport',
            myCaseRefId: cancelSurgeryData?.myCaseRef,
        });
        closeNoUsageToReportPopup(false);
        dispatch(
            closeOrderSummaryModalState({
                myCaseRef: '',
                orderSummaryModal: false,
            })
        );
        if (showSearchModal) {
            dispatch(toggleSearchResult(false));
        }
    };

    // Expose redirectToCalendarOrOrdersPage via imperative handle
    useImperativeHandle(ref, () => ({
        redirectToCalendarOrOrdersPage
    }), []);

    return (
        <React.Fragment>
            <DialogContentContainer>
                <NoUsageHeader>
                    <NoUsageTitle>
                        {t('noUsageToReport.noUsageToReportTitle')}
                    </NoUsageTitle>
                    {/* <CrossIconImg
                        path={CrossIcon}
                        onClick={redirectToCalendarOrOrdersPage}
                    /> */}
                </NoUsageHeader>
                <BackButton 
                id={`reportUsage-noUsageToReport-backButton-${cancelSurgeryData?.myCaseRef || 'unknown'}`}
                onClick={() => backBtnClickHandler(true)}>
                    <img src={ArrowLeft}></img>
                    <span>{t('noUsageToReport.BackLabel')}</span>
                </BackButton>
                {!loading ? (
                    <div>
                        <NoUsageReasonsDiv>
                            <StyledTextDiv
                                color={'#140F4B'}
                                fontSize={'15px'}
                                letterSpacing={'-0.12px'}
                                padding={'0 0 10px 10px'}
                            >
                                {t('noUsageToReport.selectNoUsageReason')}
                            </StyledTextDiv>
                            <NoUsageReasons>
                                {noUsageReasons
                                    .filter((data) =>
                                        (unidPlanStatus &&
                                            unidPlanStatus === 'Approved') ||
                                        unidPlanStatus ===
                                            'Manufacturing Complete' ||
                                        unidPlanStatus === 'In Manufacturing'
                                            ? data.optionLabel.includes('UNiD')
                                            : !data.optionLabel.includes(
                                                  'UNiD'
                                              ) && data.visibility
                                    )
                                    .map((option) => (
                                        <div
                                            className="reason"
                                            key={option.optionId}
                                        >
                                            <RadioButton
                                                isChecked={
                                                    selectedReason?.optionId ===
                                                    option.optionId
                                                }
                                                id={'reason'}
                                                label={option.optionLabel}
                                                handleChange={() =>
                                                    setSelectedReason(option)
                                                }
                                                fontFamilyStyle="AvenirNextWorld Regular"
                                            />
                                        </div>
                                    ))}
                            </NoUsageReasons>
                        </NoUsageReasonsDiv>
                        <NoUsageFooterContainer>
                            <NoUsageButtonContainer>
                                <Button
                                    id={'reportUsage-noUsageToReport-cancelButton'}
                                    show
                                    width={
                                        window.innerWidth <= sizes.mobileDevice
                                            ? '160px'
                                            : '144px'
                                    }
                                    height={'38px'}
                                    //cancel
                                    variant={ButtonStyles.cancel}
                                    // borderRadius={'19px'}
                                    margin={'0px'}
                                    // fontSize={'15px'}
                                    // fontFamily={'AvenirNextWorld Demi'}
                                    clicked={redirectToCalendarOrOrdersPage}
                                >
                                    {t('noUsageToReport.cancelButton')}
                                </Button>
                                <Button
                                id={'reportUsage-noUsageToReport-submitButton'}
                                    show
                                    width={
                                        window.innerWidth <= sizes.mobileDevice
                                            ? '160px'
                                            : '144px'
                                    }
                                    height={'38px'}
                                    // primary={selectedReason ? true : false}
                                    variant={selectedReason ? ButtonStyles?.primary : ButtonStyles.disabled}
                                    // borderRadius={'19px'}
                                    margin={
                                        window.innerWidth >
                                            sizes.mobileDevice &&
                                        '0 35px 0 13px'
                                    }
                                    // fontSize={'15px'}
                                    // fontFamily={'AvenirNextWorld Demi'}
                                    clicked={submitBtnClickHandler}
                                    disabled={!selectedReason}
                                >
                                    {t('noUsageToReport.submitButton')}
                                </Button>
                            </NoUsageButtonContainer>
                        </NoUsageFooterContainer>
                    </div>
                ) : (
                    <>
                        <Spinner
                            id="reportUsage-noUsageReasonsContainer-loadingSpinner"
                            animation="border"
                            variant="primary"
                            small
                        ></Spinner>
                    </>
                )}
            </DialogContentContainer>
        </React.Fragment>
    );
});

export default NoUsageReasonsContainer;
